import { EDeliveryDay } from '@common/enums/delivery-day.enum';
import { t } from '@common/hooks/use-t.hook';
import { consoleService } from '@common/services/console.service';
import dayjs, { Dayjs } from 'dayjs';
import { getNow } from '@common/utils/get-now/now.util';

export interface IDeliveryConfigurationCommon {
    delivery_day?: string;
    cutoff_day?: number;
    cutoff_time?: string;
}

export enum EDeliveryDayCommon {
    Mon = 'Mon',
    Tue = 'Tue',
    Wed = 'Wed',
    Thu = 'Thu',
    Fri = 'Fri',
    Sat = 'Sat',
    Sun = 'Sun',
}
export const getDayIndexFromDeliveryDay = (day: EDeliveryDayCommon | undefined): number => {
    switch (day) {
        case EDeliveryDayCommon.Mon:
            return 1;
        case EDeliveryDayCommon.Tue:
            return 2;
        case EDeliveryDayCommon.Wed:
            return 3;
        case EDeliveryDayCommon.Thu:
            return 4;
        case EDeliveryDayCommon.Fri:
            return 5;
        case EDeliveryDayCommon.Sat:
            return 6;
        case EDeliveryDayCommon.Sun:
            return 0;
    }
    consoleService.assert('not implemented', getDayIndexFromDeliveryDay + ' day = ' + day);
    return -1;
};

export const getDayjsFromCutOffTimeAndFixTimezone = (cutoff_time: string | undefined): Dayjs => {
    consoleService.assert(cutoff_time?.length === 5, 'Invalid cutoff_time should be in HH:mm format got =' + cutoff_time);
    const utsParts = cutoff_time?.split(':') || ['11', '00'];
    const result = dayjs(getNow()).set('hours', parseInt(utsParts[0], 10)).set('minutes', parseInt(utsParts[1], 10));
    return result;
};

export const parseTime = (cutoff_time: string | undefined): { hours: number; minutes: number } => {
    consoleService.assert(cutoff_time?.length === 5, 'Invalid cutoff_time should be in HH:mm format got =' + cutoff_time);
    const utsParts = cutoff_time?.split(':') || ['11', '00'];
    return {
        hours: parseInt(utsParts[0], 10),
        minutes: parseInt(utsParts[1], 10),
    };
};

export function getStartDayFromDeliveryConfig(
    today: Date,
    config: IDeliveryConfigurationCommon,
    cutOffTime: Date | undefined,
): Date {
    let { hours, minutes } = parseTime(config.cutoff_time);
    if (cutOffTime) {
        hours = cutOffTime.getHours();
        minutes = cutOffTime.getMinutes();
    }
    // const { hours, minutes } = parseTime(config.cutoff_time);
    return dayjs(getDeliveryDayFromDeliveryConfig(today, config, cutOffTime))
        .subtract(Number(config.cutoff_day) || 0, 'days')
        .set('hours', hours)
        .set('minutes', minutes)
        .set('seconds', 0)
        .set('milliseconds', 0)
        .toDate();
}

export function getDeliveryDayFromDeliveryConfig(
    today: Date,
    config: IDeliveryConfigurationCommon,
    cutOffTime: Date | undefined,
): Date {
    let { hours, minutes } = parseTime(config.cutoff_time);
    if (cutOffTime) {
        hours = cutOffTime.getHours();
        minutes = cutOffTime.getMinutes();
    }

    const d = getDayIndexFromDeliveryDay(config.delivery_day as EDeliveryDayCommon);
    let firstPossibleDay = dayjs(today).day(d);
    const minDay = dayjs(today).add(Number(config.cutoff_day) || 0, 'days');

    // const { hours, minutes } = parseTime(config.cutoff_time);
    while (
        firstPossibleDay.isBefore(minDay) ||
        (firstPossibleDay.isSame(minDay, 'days') &&
            firstPossibleDay.hour() * 60 + firstPossibleDay.minute() >= hours * 60 + minutes - 1)
    ) {
        firstPossibleDay = firstPossibleDay.add(7, 'days');
    }
    return firstPossibleDay.toDate();
}

const DAY_OF_WEEK_LABEL_MAP: Record<EDeliveryDay, string> = {
    Sun: t('Sunday'),
    Mon: t('Monday'),
    Tue: t('Tuesday'),
    Wed: t('Wednesday'),
    Thu: t('Thursday'),
    Fri: t('Friday'),
    Sat: t('Saturday'),
};

const DAY_OF_WEEK_NUMBER_MAP: Record<EDeliveryDay, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};
export const getLabelFromDayOfWeekEnum = (dayOfWeek: EDeliveryDay | undefined): string => {
    if (!dayOfWeek) {
        return '';
    }
    return DAY_OF_WEEK_LABEL_MAP[dayOfWeek];
};

export const getNumberFromDayOfWeekEnum = (dayOfWeek: EDeliveryDay | undefined): number => {
    if (!dayOfWeek) {
        return -1;
    }
    return DAY_OF_WEEK_NUMBER_MAP[dayOfWeek];
};
